import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListStatusAliasesQuery,
  useUpsertStatusAliasMutation,
  useDeleteStatusAliasMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  TASK_STATUS_META,
  type StatusAlias,
  type TaskStatus,
} from '../types/models';

interface FormData {
  alias: string;
  mapsTo: TaskStatus;
}

const STATUS_KEYS = Object.keys(TASK_STATUS_META) as TaskStatus[];

export default function StatusesPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const dispatch = useAppDispatch();

  const { data: list = [], isLoading } = useListStatusAliasesQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const [upsert] = useUpsertStatusAliasMutation();
  const [remove] = useDeleteStatusAliasMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StatusAlias | null>(null);
  const [toDelete, setToDelete] = useState<StatusAlias | null>(null);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } =
    useForm<FormData>({
      defaultValues: { alias: '', mapsTo: 'IN_PROGRESS' },
    });

  const mapsTo = watch('mapsTo');

  const existingAliasesLower = useMemo(
    () => new Set(list.map((a) => a.alias.trim().toLowerCase())),
    [list],
  );

  if (!projectId) {
    return (
      <>
        <PageHeader title="Statuses" />
        <Alert severity="info">Select a project to manage statuses.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({ alias: '', mapsTo: 'IN_PROGRESS' });
    setOpen(true);
  };

  const openEdit = (a: StatusAlias) => {
    setEditing(a);
    reset({ alias: a.alias, mapsTo: a.mapsTo });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    const alias = data.alias.trim();
    if (!alias) return;
    const lower = alias.toLowerCase();
    const collides =
      existingAliasesLower.has(lower) &&
      (!editing || editing.alias.trim().toLowerCase() !== lower);
    if (collides) {
      dispatch(
        showToast({
          message: `An alias for "${alias}" already exists.`,
          severity: 'error',
        }),
      );
      return;
    }
    try {
      await upsert({
        uid,
        projectId,
        id: editing?.id,
        alias,
        mapsTo: data.mapsTo,
      }).unwrap();
      dispatch(
        showToast({
          message: editing ? 'Alias updated' : 'Alias created',
          severity: 'success',
        }),
      );
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Alias deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Statuses"
        subtitle="Map source-system status names (Jira, etc.) onto the four internal statuses used by reports and dashboards."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New alias
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          System statuses
        </Typography>
        <Grid container spacing={1}>
          {STATUS_KEYS.map((k) => {
            const meta = TASK_STATUS_META[k];
            return (
              <Grid item xs={12} sm={6} md={3} key={k}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderLeft: `4px solid ${meta.color}`,
                    borderRadius: 1,
                  }}
                >
                  <Box component="span">{meta.icon}</Box>
                  <Typography variant="body2" fontWeight={600}>
                    {meta.label}
                  </Typography>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          These four are fixed. Add aliases below so imports recognise other terms
          (e.g. <em>UAT</em>, <em>Code Review</em>, <em>On Hold</em>) and route them
          to the right system status.
        </Typography>
      </Paper>

      {!isLoading && list.length === 0 ? (
        <EmptyState
          title="No status aliases yet"
          description="Add aliases to map external status names onto the four internal statuses."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add alias
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Alias (from source)</TableCell>
                <TableCell>Maps to</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((a) => {
                const meta = TASK_STATUS_META[a.mapsTo];
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{a.alias}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${meta.icon} ${meta.label}`}
                        sx={{ bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setToDelete(a)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit alias' : 'New status alias'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Alias text"
                autoFocus
                placeholder="e.g. UAT, Code Review, On Hold"
                helperText={
                  errors.alias?.message ??
                  'Case-insensitive. Matches the status column value from your CSV.'
                }
                error={!!errors.alias}
                {...register('alias', {
                  required: 'Required',
                  validate: (v) => v.trim().length > 0 || 'Required',
                })}
              />
              <TextField
                select
                label="Maps to"
                value={mapsTo}
                onChange={(e) => setValue('mapsTo', e.target.value as TaskStatus)}
              >
                {STATUS_KEYS.map((k) => {
                  const meta = TASK_STATUS_META[k];
                  return (
                    <MenuItem key={k} value={k}>
                      <Box component="span" sx={{ mr: 1 }}>
                        {meta.icon}
                      </Box>
                      {meta.label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete alias?"
        message={`"${toDelete?.alias}" will no longer be recognised on import.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
